'use client';

import classNames from 'classnames';
import { BaseTranslations } from '../../../../baseTranslations';
import { CmsProduct } from '../../../../types/cmsProduct';
import { useCmsClientContext } from '../../../../utils/cmsPageContext';
import { getPolicyDocuments } from '../../../../utils/tiAttributes';
import { getClientTranslations } from '../../../../utils/translations/getClientTranslations';
import { tr } from '../../../../utils/translations/tr';
import { MaterialIcon } from '../../../MaterialIcon';
import { ProductDetailsSectionHeader } from '../../BaseComponents/ProductDetailsSectionHeader';
import { ProductDetailsSectionWrapper } from '../../BaseComponents/ProductDetailsSectionWrapper';

type PolicyDocumentEntry = { label: string; url: string };

/**
 * TI provider page Policy Documents section (M6, net-new). Renders up to two
 * links (Policy Wording, Brochure) for the active tier's product as a card of
 * uppercase rows, each opening in a new tab (open-in-new affordance). A single
 * populated link renders on its own; when both are empty the section renders
 * nothing (CMS-1644 AC-9). A rendered section but NOT a nav link (CMS-1643 §3).
 * Styling per Figma node 14249:1272.
 */
export const ProductDetailsSectionPolicyDocuments: React.FC<{
  product?: CmsProduct;
}> = ({ product }) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const { policyWording, brochure } = getPolicyDocuments(product);

  const entries: PolicyDocumentEntry[] = [
    ...(policyWording
      ? [{ label: tr('policy_wording', staticText), url: policyWording }]
      : []),
    ...(brochure ? [{ label: tr('brochure', staticText), url: brochure }] : []),
  ];

  if (entries.length === 0) return null;

  return (
    <div className="flex flex-col gap-6">
      <ProductDetailsSectionHeader
        icon={<MaterialIcon icon="description" size={3} />}
      >
        {tr('policy_documents', staticText)}
      </ProductDetailsSectionHeader>
      <ProductDetailsSectionWrapper>
        {entries.map((entry, index) => (
          // text-text/accent cascades to both the label and the open_in_new
          // glyph (Material Symbols render in currentColor).
          <a
            key={entry.url}
            href={entry.url}
            target="_blank"
            rel="noopener noreferrer"
            className={classNames(
              'flex items-center justify-between gap-spacing/gap/x px-spacing/padding/x py-spacing/padding/y-sm text-text/accent',
              {
                'border-b border-borders/default': index !== entries.length - 1,
              },
            )}
          >
            <span className="mhg-alpha-tag-font uppercase">{entry.label}</span>
            <MaterialIcon icon="open_in_new" size={3} className="shrink-0" />
          </a>
        ))}
      </ProductDetailsSectionWrapper>
    </div>
  );
};
